import { useMemo } from 'react';
import styled from 'styled-components';

import { BalanceView } from '#/features/balance/balance-view';
import { useBalancesView } from '#/features/balance/balance-context';
import { useDepositAction } from '#/features/deposit/useDepositAction';
import { useWithdrawAction } from '#/features/withdraw/useWithdrawAction';

import { cFFFFFF } from '#/colors';
import { Loadable } from '#/components/shared/Loadable';
import ButtonLink from '#/components/shared/ButtonLink';
import { VerticalSpacer } from '#/components/shared/Spacer';
import AssetIcon from '#/components/shared/AssetIcon';

import { TableSpec, createTradeOverviewTable } from './table';
import { Empty, Error } from './atoms';

const DEFAULT_TABLE_SPEC = {
  asset: { header: 'Asset', minWidth: '260px' },
  walletBalance: {
    header: 'Wallet Balance',
    minWidth: '160px',
    width: '100%',
  },
  actions: { header: 'Actions', minWidth: '168px' },
};

interface Props {
  tableSpec?: TableSpec<string>;
}

export default function Collateral(props: Props) {
  const { tableSpec = DEFAULT_TABLE_SPEC } = props;
  const [Table, Row] = useMemo(
    () => createTradeOverviewTable(tableSpec),
    [tableSpec],
  );
  const { status, balances, error } = useBalancesView();

  if (status === 'error') {
    return <Error>{error}</Error>;
  }

  const isEmpty = balances.length === 0;
  const isLoading = status === 'loading';

  if (isEmpty) {
    return (
      <Loadable isLoading={isLoading}>
        <Empty>You have no collateral</Empty>
      </Loadable>
    );
  }

  return (
    <Loadable isLoading={isLoading}>
      <Table>
        {balances.map((balance) => (
          <Row
            key={balance.id}
            asset={<BalanceAsset balance={balance} />}
            walletBalance={balance.walletBalance}
            actions={<BalanceActions balance={balance} />}
          />
        ))}
      </Table>
    </Loadable>
  );
}

function BalanceAsset(props: { balance: BalanceView }) {
  const { balance } = props;

  return (
    <BalanceAssetContainer>
      <AssetIcon asset={balance.asset} size={21} />
      {balance.asset}
    </BalanceAssetContainer>
  );
}

const BalanceAssetContainer = styled.div`
  line-height: 0px;
  display: grid;
  align-items: center;
  grid-template-columns: 21px 1fr;
  gap: 8px;
  color: ${cFFFFFF};
`;

function BalanceActions(props: { balance: BalanceView }) {
  const { balance } = props;
  const depositAction = useDepositAction();
  const withdrawAction = useWithdrawAction();

  if (balance.asset !== 'USDC') {
    return null;
  }

  return (
    <BalanceActionsContainer>
      <ButtonLink
        onClick={depositAction.execute}
        disabled={depositAction.isDisabled}
      >
        Deposit
      </ButtonLink>
      <VerticalSpacer />
      <ButtonLink
        onClick={withdrawAction.execute}
        disabled={withdrawAction.isDisabled}
      >
        Withdraw
      </ButtonLink>
    </BalanceActionsContainer>
  );
}

const BalanceActionsContainer = styled.div`
  height: 28px;
  display: grid;
  align-items: center;
  grid-template-columns: repeat(3, min-content);
  gap: 12px;
`;
